import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ICurrency } from '../../model/currency.model';
import {
  ICurrentOrganization,
  IOrganization,
  IOrganizationType,
  ITax
} from '../../model/organization.model';
import { ISibasiHTTPResponse } from '../../model/sibasiHTTPResponse.model';
import { IMember } from '../../model/members.model';
import { IPagination } from '../../model/util.model';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  constructor(private http: HttpClient) {}
  public addOrganization(organization: {}): Observable<
    ISibasiHTTPResponse<IOrganization>
  > {
    return this.http.post<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}organization`,
      organization
    );
  }
  public editOrganization(organization: {}): Observable<
    ISibasiHTTPResponse<IOrganization>
  > {
    return this.http.post<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}organizationadmin`,
      organization
    );
  }

  public getOrganizationById(
    organizationId: any
  ): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.http.get<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}organization/${organizationId}`
    );
  }
  public getAllOrganizationProjects(
    organizationId: any
  ): Observable<ISibasiHTTPResponse<[]>> {
    return this.http.get<ISibasiHTTPResponse<[]>>(
      `${environment.api_url}monival/org/all/${organizationId}/projects`
    );
  }
  public getAllOrganizations(): Observable<ISibasiHTTPResponse<IOrganization[]>> {
    return this.http.get<ISibasiHTTPResponse<IOrganization[]>>(
      `${environment.api_url}organization`
    );
  }
  public optOutOfOrganization(
    organizationId: string | undefined
  ): Observable<ISibasiHTTPResponse<IOrganization[]>> {
    return this.http.get<ISibasiHTTPResponse<IOrganization[]>>(
      `${environment.api_url}organization/membership/optOut/${organizationId}`
    );
  }

  deleteOrganization(
    organizationId: string | undefined
  ): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.http.delete<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}organizationadmin`
    );
  }

  public getCurrencies(): Observable<ISibasiHTTPResponse<ICurrency[]>> {
    return this.http.get<ISibasiHTTPResponse<ICurrency[]>>(
      `${environment.api_url}currency`
    );
  }

  public getCurrenciesWIthFormat(): Observable<
    ISibasiHTTPResponse<{ label: any; value: any }[]>
  > {
    return this.http
      .get<ISibasiHTTPResponse<ICurrency[]>>(`${environment.api_url}currency`)
      .pipe(
        map((res) => {
          const currencyFormat = res.result.map((x) => ({
            value: {
              symbol: x.symbol ? x.symbol : null,
              code: x.code
            },
            label: x.country + ' (' + x.code + ')'
          }));
          const currencyAsReturnObj: ISibasiHTTPResponse<{ label: any; value: any }[]> = {
            message: res.message,
            result: currencyFormat,
            error: undefined
          };
          return currencyAsReturnObj;
        })
      );
  }

  public async checkIfUserHasCurrentOrganization(): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.get<ISibasiHTTPResponse<IOrganization>>(
        `${environment.api_url}organization/current`
      )
    );
    if (response.result) {
      return true;
    }
    return false;
  }

  public async checkIfUserIsSySAdmin(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.get<ISibasiHTTPResponse<IOrganization>>(
          `${environment.api_url}monival/org/isAdmin`
        )
      );
      if (response.result) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  public getCurrentOrganization(): Observable<ISibasiHTTPResponse<ICurrentOrganization>> {
    return this.http.get<ISibasiHTTPResponse<ICurrentOrganization>>(
      `${environment.api_url}organization/current`
    );
  }

  public setCurrentOrganization(
    organizationId: string
  ): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.http.get<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}organization/set/${organizationId}`
    );
  }

  public addMembersToOrganization(
    organizationId: any,
    members: any
  ): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.http.post<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}sibasi-organization/${organizationId}/member`,
      members
    );
  }
  removeMemberFromOrganization(
    id: string,
    memberId: string
  ): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.http.delete<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}sibasi-organization/${id}/member/${memberId}`
    );
  }

  //********************Organization Type ****************/
  getOrganizationTypes(): Observable<ISibasiHTTPResponse<IOrganizationType[]>> {
    return this.http.get<ISibasiHTTPResponse<IOrganizationType[]>>(
      `${environment.api_url}orgTypes`
    );
  }

  public getOrganizationPrimaryBusiness(): Observable<
    ISibasiHTTPResponse<IOrganizationType[]>
  > {
    return this.http.get<ISibasiHTTPResponse<IOrganizationType[]>>(
      `${environment.api_url}organizationPrimaryBusiness`
    );
  }

  getOrganizationActvatedModule(): Observable<ISibasiHTTPResponse<ICurrency>> {
    return this.http.get<ISibasiHTTPResponse<ICurrency>>(
      `${environment.api_url}activatedModule`
    );
  }

  getOrganizationMembers(): Observable<ISibasiHTTPResponse<IMember[]>> {
    return this.http.get<ISibasiHTTPResponse<IMember[]>>(
      `${environment.api_url}organization/members/all`
    );
  }

  //********************Organization Admin ****************/

  getAllOrganizationsPagination(
    paginationParams: any
  ): Observable<ISibasiHTTPResponse<IPagination<IOrganization[]>>> {
    return this.http.post<ISibasiHTTPResponse<IPagination<IOrganization[]>>>(
      `${environment.api_url}organization-sys-admin/all/paginated/union`,
      paginationParams
    );
  }

  isOrganizationPremium(): Observable<boolean> {
    return this.getCurrentOrganization().pipe(
      map((res) => {
        const isPremium: boolean = res.result.organization.isActiveLicense;
        return isPremium;
      })
    );
  }

  // PAYMENT INTEGRATION

  public addPaymentIntegration(
    integrationDetails: any
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}OrganizationSecureStore`,
      integrationDetails
    );
  }
  public isOrganizationMpesaIntegrated(): Observable<ISibasiHTTPResponse<boolean>> {
    return this.http.get<ISibasiHTTPResponse<boolean>>(
      `${environment.api_url}OrganizationSecureStore`
    );
  }
  public mpesaStkPush(integrationDetails: any): Observable<ISibasiHTTPResponse<any>> {
    return this.http.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}transactionPaymentRecord/stkPush/process`,
      integrationDetails
    );
  }

  // ************************************* Taxes service******************************************************

  public getAllOrganizationTaxes(): Observable<ISibasiHTTPResponse<ITax[]>> {
    return this.http.get<ISibasiHTTPResponse<ITax[]>>(`${environment.api_url}tax/all`);
  }

  public getOrganizationTaxById(taxTypeId: string | undefined) {
    return this.http.get(`${environment.api_url}tax/${taxTypeId}`);
  }

  public addOrganizationTax(organizationTax: ITax) {
    return this.http.post<ISibasiHTTPResponse<ITax>>(
      `${environment.api_url}tax`,
      organizationTax
    );
  }
  public editOrganizationTax(organizationTaxId: string, organizationTax: ITax) {
    return this.http.post<ISibasiHTTPResponse<ITax>>(
      `${environment.api_url}tax/${organizationTaxId}`,
      organizationTax
    );
  }

  public getOrganizationTaxes(): Observable<ISibasiHTTPResponse<ITax[]>> {
    return this.http.get<ISibasiHTTPResponse<ITax[]>>(`${environment.api_url}tax/all`);
  }

  public deleteTaxType(taxTypeId: string | undefined) {
    return this.http.delete(`${environment.api_url}tax/${taxTypeId}`);
  }

  public getAllPaymentTerms(): Observable<ISibasiHTTPResponse<[]>> {
    return this.http.get<ISibasiHTTPResponse<[]>>(
      `${environment.api_url}organization/paymentTerms/all`
    );
  }

  public getAllPaymentTermsPaginated(): Observable<ISibasiHTTPResponse<string[]>> {
    return this.http
      .get<
        ISibasiHTTPResponse<string[]>
      >(`${environment.api_url}organization/paymentTerms/all`)
      .pipe(
        map((res) => {
          return {
            ...res,
            result: {
              ...res.result,
              data: res.result.map((term) => ({
                value: term,
                label: term
              }))
            }
          };
        })
      );
  }

  public getUserOrganizationMemberRoles() {
    return this.http.get(`${environment.api_url}organization/roles/all`);
  }

  public getAllUploadHistory(): Observable<ISibasiHTTPResponse<[]>> {
    return this.http.get<ISibasiHTTPResponse<[]>>(
      `${environment.api_url}bulkUploadBatch`
    );
  }

  deleteUploadBatch(batchId: string): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.http.delete<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}bulkUploadBatch/${batchId}`
    );
  }
}
